import React, { useState, useEffect, useRef } from 'react';
import { Product, Order, User } from '../types';
import { api } from '../constants';
import Toast from '../components/Toast';
import Modal from '../components/Modal';

const Admin: React.FC<{ onNavigate: (page: string) => void }> = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState<'inventory' | 'orders' | 'clients'>('inventory');
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [clients, setClients] = useState<User[]>([]);
  const [selectedClient, setSelectedClient] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Notification state
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Confirmation Modal state
  const [confirmModal, setConfirmModal] = useState<{
      isOpen: boolean;
      title: string;
      message: string;
      onConfirm: () => void;
      type: 'danger' | 'info';
  }>({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: () => {},
      type: 'info'
  });

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
      setNotification({ message, type });
  };

  // Filters State
  const [filters, setFilters] = useState({
      inventory: { search: '', category: 'ALL', stock: 'ALL', promo: 'ALL', status: 'ALL' },
      orders: { search: '', status: 'ALL' },
      clients: { search: '', status: 'ALL', tag: 'ALL' }
  });

  const [stats, setStats] = useState({
      totalRevenue: 0, activeOrders: 0, lowStock: 0, totalClients: 0
  });

  // Helper: Check for differences between two product objects
  const isProductDifferent = (p1: Partial<Product>, p2: Partial<Product>) => {
      const keys: (keyof Product)[] = ['name', 'category', 'price', 'costPrice', 'stock', 'onHold', 'onPromotion', 'discount', 'isActive'];
      return keys.some(k => {
          // Loose comparison for numbers to handle string/number diffs from CSV
          // Treat null/undefined/empty string as equivalent
          const v1 = p1[k] ?? '';
          const v2 = p2[k] ?? '';
          return String(v1) !== String(v2);
      });
  };

  const filteredProducts = products.filter(p => {
      const matchesSearch = (p.name || '').toLowerCase().includes(filters.inventory.search.toLowerCase());
      const matchesCat = filters.inventory.category === 'ALL' || p.category === filters.inventory.category;
      const matchesStock = filters.inventory.stock === 'ALL' || 
          (filters.inventory.stock === 'LOW' && p.stock < 10 && p.stock > 0) ||
          (filters.inventory.stock === 'OUT' && p.stock === 0);
      const matchesPromo = filters.inventory.promo === 'ALL' || 
          (filters.inventory.promo === 'YES' && p.onPromotion) ||
          (filters.inventory.promo === 'NO' && !p.onPromotion);
      const matchesStatus = filters.inventory.status === 'ALL' ||
          (filters.inventory.status === 'ACTIVE' && p.isActive !== false) ||
          (filters.inventory.status === 'INACTIVE' && p.isActive === false);
      return matchesSearch && matchesCat && matchesStock && matchesPromo && matchesStatus;
  });

  const filteredOrders = orders.filter(o => {
      const search = (filters.orders.search || '').toLowerCase();
      const matchesSearch = (o.id || '').toLowerCase().includes(search) || 
                          (o.businessName || '').toLowerCase().includes(search);
      const matchesStatus = filters.orders.status === 'ALL' || o.status === filters.orders.status;
      return matchesSearch && matchesStatus;
  });

  const filteredClients = clients.filter(c => {
      const search = (filters.clients.search || '').toLowerCase();
      const matchesSearch = (c.businessName || '').toLowerCase().includes(search) || 
                          (c.email || '').toLowerCase().includes(search);
      const matchesStatus = filters.clients.status === 'ALL' || (c.status || 'ACTIVE') === filters.clients.status;
      const matchesTag = filters.clients.tag === 'ALL' || (c.tags || []).includes(filters.clients.tag);
      return matchesSearch && matchesStatus && matchesTag;
  });

  // Get all unique tags for filter
  const [editingProduct, setEditingProduct] = useState<Partial<Product> | null>(null);
  const [savingStatus, setSavingStatus] = useState<string | null>(null);
  const [savingClient, setSavingClient] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const fileImportRef = useRef<HTMLInputElement>(null);

  // Bulk Import State
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState<{ status: 'NEW' | 'UPDATE' | 'DEACTIVATE' | 'NO_CHANGE', data: Product, originalData?: Product }[]>([]);
  const [processingImport, setProcessingImport] = useState(false);

  const parseCSV = (text: string) => {
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) return [];
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      
      const re = /,(?=(?:(?:[^"]*"){2})*[^"]*$)/; // Split by comma ignoring quotes
      return lines.slice(1).map(line => {
          const values = line.split(re).map(val => val.trim().replace(/^"|"$/g, '').replace(/""/g, '"'));
          const entry: any = {};
          headers.forEach((h, i) => {
              if (values[i] !== undefined && values[i] !== '') {
                  // Type conversion
                  if (['price', 'costPrice', 'discount'].includes(h)) {
                      const val = parseFloat(values[i]);
                      entry[h] = isNaN(val) ? 0 : val;
                  }
                  else if (['stock', 'onHold'].includes(h)) {
                      const val = parseInt(values[i]);
                      entry[h] = isNaN(val) ? 0 : val;
                  }
                  else if (['onPromotion', 'isActive'].includes(h)) entry[h] = values[i].toLowerCase() === 'true';
                  else entry[h] = values[i];
              }
          });
          return entry;
      });
  };

  const handleDownloadTemplate = () => {
      const headers = ['id', 'name', 'description', 'price', 'costPrice', 'category', 'stock', 'onHold', 'onPromotion', 'discount', 'image', 'isActive'];
      const csvContent = [
          headers.join(','),
          ...products.map(p => headers.map(h => {
              const val = (p as any)[h];
              if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
              return val ?? '';
          }).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const text = await file.text();
      const importedItems = parseCSV(text);
      
      // Create maps for efficient lookup
      const existingById = new Map(products.map(p => [p.id, p]));
      const existingByName = new Map(products.map(p => [p.name.toLowerCase().trim(), p]));
      
      const preview: typeof importPreview = [];
      const processedIds = new Set<string>();

      // 1. Process Imported Items
      importedItems.forEach((item: any) => {
          if (!item.name) return;
          
          const nameKey = item.name.toLowerCase().trim();
          let existingProduct: Product | undefined;
          
          // Try matching by ID first, then by Name
          if (item.id && existingById.has(item.id)) {
              existingProduct = existingById.get(item.id);
          } else if (existingByName.has(nameKey)) {
              existingProduct = existingByName.get(nameKey);
          }

          if (existingProduct) {
              const updatedData = { ...existingProduct, ...item, id: existingProduct.id };
              // Compare new data with existing
              const changed = isProductDifferent(updatedData, existingProduct);
              
              preview.push({ 
                  status: changed ? 'UPDATE' : 'NO_CHANGE', 
                  data: updatedData,
                  originalData: existingProduct
              });
              processedIds.add(existingProduct.id);
          } else {
              // Truly new product or duplicate with new ID
              const needsNewId = !item.id || processedIds.has(item.id);
              const finalId = needsNewId ? 'p_' + Date.now() + Math.random().toString(36).substr(2, 5) : item.id;
              
              preview.push({ 
                  status: 'NEW', 
                  data: { ...item, id: finalId, isActive: true } as Product 
              });
              processedIds.add(finalId);
          }
      });

      // 2. Identify Missing Items (Deactivate)
      products.forEach(p => {
          if (!processedIds.has(p.id)) {
              // If already inactive, it's NO_CHANGE, otherwise DEACTIVATE
              const status = p.isActive === false ? 'NO_CHANGE' : 'DEACTIVATE';
              preview.push({ 
                  status, 
                  data: { ...p, isActive: false },
                  originalData: p
              });
          }
      });

      setImportPreview(preview);
      setShowImportModal(true);
      if (fileImportRef.current) fileImportRef.current.value = '';
  };

  const updatePreviewItem = (index: number, field: keyof Product, value: any) => {
      const newPreview = [...importPreview];
      const item = newPreview[index];
      
      // Update the field
      item.data = { ...item.data, [field]: value };

      // SAFETY: Never allow ID to be empty
      if (!item.data.id) {
          item.data.id = 'p_' + Date.now() + Math.random().toString(36).substr(2, 5);
      }

      // Status Logic Adjustment
      if (item.originalData) {
          // Re-evaluate against original data
          const changed = isProductDifferent(item.data, item.originalData);
          if (field === 'isActive' && value === false && item.originalData.isActive !== false) {
             item.status = 'DEACTIVATE';
          } else {
             item.status = changed ? 'UPDATE' : 'NO_CHANGE';
          }
      } else {
          // It's a new item
          item.status = 'NEW';
      }

      setImportPreview(newPreview);
  };

  const handleConfirmImport = async () => {
      setProcessingImport(true);
      try {
          // Filter out NO_CHANGE items to reduce load, but handle deactivations
          const itemsToProcess = importPreview.filter(item => item.status !== 'NO_CHANGE');
          
          if (itemsToProcess.length === 0) {
              showToast('No changes detected to sync.', 'info');
              setShowImportModal(false);
              return;
          }

          // Deduplicate by ID: DynamoDB BatchWriteItem fails if multiple items in one batch have the same key
          const deduplicatedMap = new Map<string, Product>();
          
          itemsToProcess.forEach(item => {
              // If status is DEACTIVATE, force isActive false. For others, trust the data.
              const isActive = item.status === 'DEACTIVATE' ? false : (item.data.isActive ?? true);
              const data = { ...item.data, isActive };
              
              // Ensure we have a valid ID
              if (!data.id) {
                  data.id = 'p_' + Date.now() + Math.random().toString(36).substr(2, 5);
              }
              
              // Latest item wins if there's a duplicate ID
              deduplicatedMap.set(data.id, data);
          });

          const payload = Array.from(deduplicatedMap.values());

          const baseUrl = (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/$/, '');
          const response = await fetch(`${baseUrl}/products`, {
              method: 'POST',
              headers: { 
                  'Content-Type': 'application/json', 
                  'Authorization': `Bearer ${localStorage.getItem('auth_token')}` 
              },
              body: JSON.stringify(payload)
          });

          if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(errorData.error || 'Batch import failed');
          }
          
          showToast(`Successfully synced ${payload.length} changes.`);
          setShowImportModal(false);
          fetchAllData();
      } catch (e: any) {
          showToast(e.message, 'error');
      } finally {
          setProcessingImport(false);
      }
  };

  useEffect(() => { fetchAllData(); }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const baseUrl = (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/$/, '');
      const token = localStorage.getItem('auth_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [prodData, ordersRes, usersRes] = await Promise.all([
          api.getProducts(),
          fetch(`${baseUrl}/orders`, { headers }),
          fetch(`${baseUrl}/users`, { headers })
      ]);

      const productsList = Array.isArray(prodData) ? prodData : [];
      setProducts(productsList);

      let ordersList: Order[] = [];
      if (ordersRes.ok) {
          const data = await ordersRes.json();
          ordersList = Array.isArray(data) ? data : [];
          setOrders(ordersList);
      }

      let clientList: User[] = [];
      if (usersRes.ok) {
          const data = await usersRes.json();
          clientList = Array.isArray(data) ? data : [];
          setClients(clientList);
      }

      setStats({
          totalRevenue: ordersList.reduce((acc, curr) => acc + (curr.total || 0), 0),
          activeOrders: ordersList.filter(o => o.status !== 'COMPLETED' && o.status !== 'CANCELLED').length,
          lowStock: productsList.filter(p => p.stock < 10).length,
          totalClients: clientList.length
      });
    } catch (err: any) { 
        showToast(err.message, 'error');
    }
    finally { setLoading(false); }
  };

  const handleToggleUser = async (user: User) => {
      try {
          await api.updateUser(user.id, { status: user.status === 'DISABLED' ? 'ACTIVE' : 'DISABLED' });
          showToast(`Account for ${user.businessName} ${user.status === 'DISABLED' ? 'restored' : 'suspended'}.`);
          fetchAllData();
      } catch (e: any) { showToast(e.message, 'error'); }
  };

  const handleToggleAdmin = async (user: User) => {
      setConfirmModal({
          isOpen: true,
          title: user.role === 'ADMIN' ? 'Revoke Admin Rights' : 'Grant Admin Rights',
          message: `Are you sure you want to ${user.role === 'ADMIN' ? 'revoke admin rights from' : 'grant admin rights to'} ${user.businessName || user.email}?`,
          type: user.role === 'ADMIN' ? 'danger' : 'info',
          onConfirm: async () => {
              try {
                  const newRole = user.role === 'ADMIN' ? 'CLINIC' : 'ADMIN';
                  await api.updateUser(user.id, { role: newRole });
                  showToast(`Permissions updated for ${user.businessName || user.email}.`);
                  fetchAllData();
                  if (selectedClient?.id === user.id) {
                      setSelectedClient({ ...selectedClient, role: newRole });
                  }
              } catch (e: any) { showToast(e.message, 'error'); }
          }
      });
  };

  const handleUpdateClientData = async (userId: string, data: any) => {
      try {
          const updated = await api.updateUser(userId, data);
          setClients(clients.map(c => c.id === userId ? updated : c));
          if (selectedClient?.id === userId) setSelectedClient(updated);
          showToast('Client data updated.');
      } catch (e: any) { showToast(e.message, 'error'); }
  };

  const handleSaveClientDetail = async () => {
      if (!selectedClient) return;
      setSavingClient(true);
      try {
          const { id, tags, adminNotes } = selectedClient;
          // Ensure tags is always an array to prevent undefined issues in backend update
          const updatedUser = await api.updateUser(id, { tags: tags || [], adminNotes });
          
          // Update local state immediately for instant UI feedback
          setClients(clients.map(c => c.id === id ? updatedUser : c));
          
          setSelectedClient(null);
          showToast('Client profile synced successfully.');
          // Still fetch all data in background to keep everything perfectly in sync
          fetchAllData();
      } catch (e: any) {
          showToast(e.message, 'error');
      } finally {
          setSavingClient(false);
      }
  };

    const [newTagInput, setNewTagInput] = useState('');

  const handleAddTag = () => {
      if (!selectedClient) return;
      const val = newTagInput.trim();
      if (val && !(selectedClient.tags || []).includes(val)) {
          setSelectedClient({...selectedClient, tags: [...(selectedClient.tags || []), val]});
          setNewTagInput('');
      }
  };

  const allTags = Array.from(new Set(clients.flatMap(c => c.tags || [])));

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video' | 'document') => {
      const file = e.target.files?.[0];
      if (!file || !editingProduct) return;

      setUploading(true);
      try {
          const baseUrl = (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/$/, '');
          const token = localStorage.getItem('auth_token');
          
          const res = await fetch(`${baseUrl}/media/upload`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ fileName: file.name, contentType: file.type })
          });
          const { uploadUrl, fileUrl } = await res.json();

          // Use an extremely clean fetch for S3 to avoid any signature mismatch
          // Since we simplified signing to only include 'host', we send NO headers
          await fetch(uploadUrl, { 
              method: 'PUT', 
              body: file,
              mode: 'cors',
              credentials: 'omit'
          });

          if (type === 'video') {
              setEditingProduct({ ...editingProduct, video: fileUrl });
          } else if (type === 'document') {
              const currentDocs = editingProduct.documents || [];
              setEditingProduct({
                  ...editingProduct,
                  documents: [...currentDocs, { name: file.name, url: fileUrl }]
              });
          } else {
              const currentImages = editingProduct.images || [];
              setEditingProduct({
                  ...editingProduct,
                  image: editingProduct.image || fileUrl,
                  images: [...currentImages, fileUrl]
              });
          }
          showToast(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully.`);
      } catch (err) { showToast("Upload failed", 'error'); }
      finally { setUploading(false); }
  };

  const removeImage = (url: string) => {
      if (!editingProduct) return;
      const updatedImages = (editingProduct.images || []).filter(i => i !== url);
      setEditingProduct({
          ...editingProduct,
          images: updatedImages,
          image: editingProduct.image === url ? (updatedImages[0] || '') : editingProduct.image
      });
  };

  const handleUpdateOrder = async (orderId: string, status: string, adminNote: string) => {
      setSavingStatus(orderId);
      try {
          const baseUrl = (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/$/, '');
          await fetch(`${baseUrl}/orders`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
              body: JSON.stringify({ orderId, status, adminNote })
          });
          showToast(`Order ${orderId} updated.`);
          fetchAllData();
      } catch (e: any) { showToast(e.message, 'error'); }
      finally { setSavingStatus(null); }
  };

  const handleToggleProductStatus = async (product: Product) => {
      try {
          const baseUrl = (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/$/, '');
          const newStatus = !(product.isActive !== false); // If undefined or true, new status is false. If false, new status is true.
          
          await fetch(`${baseUrl}/products`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
              body: JSON.stringify({ ...product, isActive: newStatus })
          });
          
          showToast(`Product ${newStatus ? 'activated' : 'deactivated'}.`);
          // Optimistic update
          setProducts(products.map(p => p.id === product.id ? { ...p, isActive: newStatus } : p));
      } catch (e: any) { showToast(e.message, 'error'); }
  };

  const handleSaveProduct = async (e: React.FormEvent) => {
      e.preventDefault();
      try {
          const baseUrl = (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/$/, '');
          const response = await fetch(`${baseUrl}/products`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
              body: JSON.stringify(editingProduct)
          });
          if (response.ok) { 
              showToast('Product saved to database.');
              setEditingProduct(null); 
              fetchAllData(); 
          }
          else throw new Error("Save failed");
      } catch (e: any) { showToast(e.message, 'error'); }
  };

  const handleDeleteProduct = async (id: string) => {
      setConfirmModal({
          isOpen: true,
          title: 'Delete Product',
          message: 'Are you sure you want to permanently delete this product? This action cannot be undone.',
          type: 'danger',
          onConfirm: async () => {
              try {
                  const baseUrl = (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/$/, '');
                  await fetch(`${baseUrl}/products/${id}`, {
                      method: 'DELETE',
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
                  });
                  showToast('Product deleted.');
                  fetchAllData();
              } catch (e: any) { showToast(e.message, 'error'); }
          }
      });
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-8">
                <div>
                    <h1 className="text-5xl font-black text-slate-900 tracking-tight mb-2">Command Center</h1>
                    <p className="text-slate-500 font-medium">Manage your dental supply empire from one central dashboard.</p>
                </div>
                <div className="flex bg-white/50 backdrop-blur-md p-1.5 rounded-2xl border border-slate-200 shadow-sm w-full md:w-auto overflow-x-auto">
                    {(['inventory', 'orders', 'clients'] as const).map(tab => (
                        <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 md:flex-none px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap ${activeTab === tab ? 'bg-slate-900 text-white shadow-lg scale-105' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}>{tab}</button>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[ { label: 'Total Revenue', value: `$${stats.totalRevenue.toLocaleString()}`, icon: '💰' }, { label: 'Active Orders', value: stats.activeOrders, icon: '📦' }, { label: 'Low Stock Items', value: stats.lowStock, icon: '⚠️' }, { label: 'Total Clients', value: stats.totalClients, icon: '👥' } ].map((s, idx) => (
                    <div key={idx} className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                        <div className="text-2xl mb-2">{s.icon}</div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{s.label}</div>
                        <div className="text-2xl font-black text-slate-900">{s.value}</div>
                    </div>
                ))}
            </div>
        </div>

        {notification && (
            <Toast 
                message={notification.message} 
                type={notification.type} 
                onClose={() => setNotification(null)} 
            />
        )}

        <Modal 
            isOpen={confirmModal.isOpen}
            title={confirmModal.title}
            message={confirmModal.message}
            type={confirmModal.type}
            onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
            onConfirm={confirmModal.onConfirm}
        />

        {loading ? (
            <div className="flex flex-col items-center justify-center py-32 space-y-4"><div className="animate-spin h-12 w-12 border-4 border-slate-900 border-t-transparent rounded-full"></div><span className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Syncing Data...</span></div>
        ) : (
            <div className="bg-white rounded-[2.5rem] shadow-2xl border border-slate-100 overflow-hidden">
                {activeTab === 'inventory' && (
                    <div className="p-10">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
                            <div><h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Product Inventory</h2><p className="text-slate-400 text-sm">Control your stock levels and pricing</p></div>
                            <div className="flex gap-2">
                                <button onClick={handleDownloadTemplate} className="bg-slate-100 text-slate-600 px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-all">⬇ Export CSV</button>
                                <button onClick={() => fileImportRef.current?.click()} className="bg-slate-900 text-white px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-black transition-all">⬆ Import CSV</button>
                                <input type="file" ref={fileImportRef} className="hidden" accept=".csv" onChange={handleFileSelect} />
                                <button onClick={() => setEditingProduct({ images: [] })} className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-200 hover:bg-blue-700 hover:-translate-y-0.5 transition-all">+ Add Product</button>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8 bg-slate-50 p-6 rounded-3xl border border-slate-100">
                            <input type="text" placeholder="Search product name..." className="px-4 py-2 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium" value={filters.inventory.search} onChange={e => setFilters({...filters, inventory: {...filters.inventory, search: e.target.value}})} />
                            <select className="px-4 py-2 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold uppercase tracking-wider" value={filters.inventory.category} onChange={e => setFilters({...filters, inventory: {...filters.inventory, category: e.target.value}})}><option value="ALL">All Categories</option><option value="CLINIC">Clinic</option><option value="LAB">Lab</option></select>
                            <select className="px-4 py-2 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold uppercase tracking-wider" value={filters.inventory.stock} onChange={e => setFilters({...filters, inventory: {...filters.inventory, stock: e.target.value}})}><option value="ALL">Stock Level: All</option><option value="LOW">Low Stock (&lt;10)</option><option value="OUT">Out of Stock</option></select>
                            <select className="px-4 py-2 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold uppercase tracking-wider" value={filters.inventory.promo} onChange={e => setFilters({...filters, inventory: {...filters.inventory, promo: e.target.value}})}><option value="ALL">Promotion: All</option><option value="YES">On Promotion</option><option value="NO">No Promotion</option></select>
                            <select className="px-4 py-2 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold uppercase tracking-wider" value={filters.inventory.status} onChange={e => setFilters({...filters, inventory: {...filters.inventory, status: e.target.value}})}><option value="ALL">Status: All</option><option value="ACTIVE">Active</option><option value="INACTIVE">Inactive</option></select>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left min-w-[800px]">
                                <thead><tr className="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] border-b border-slate-100"><th className="pb-6">Product Information</th><th className="pb-6">Category</th><th className="pb-6 text-right">Unit Price</th><th className="pb-6 text-right">Unit Cost</th><th className="pb-6 text-center">Available</th><th className="pb-6 text-center">Reserved</th><th className="pb-6 text-center">Total Asset</th><th className="pb-6 text-center">Status</th><th className="pb-6 text-right">Manage</th></tr></thead>
                                <tbody className="text-sm">
                                    {filteredProducts.map(p => (
                                        <tr key={p.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors">
                                            <td className="py-6"><div className="flex items-center gap-4"><img src={p.image} className="w-12 h-12 rounded-xl object-cover border border-slate-100 shadow-sm" alt="" /><div><div className="font-black text-slate-900">{p.name}</div>{p.onPromotion && <span className="text-[9px] bg-red-500 text-white px-2 py-0.5 rounded-full font-black uppercase tracking-tighter mt-1 inline-block">Sale {p.discount}%</span>}</div></div></td>
                                            <td className="py-6 font-bold text-slate-500 uppercase text-[10px] tracking-widest">{p.category}</td>
                                            <td className="py-6 text-right font-mono font-black text-blue-600">${p.price.toFixed(2)}</td>
                                            <td className="py-6 text-right font-mono text-slate-400">${(p.costPrice || 0).toFixed(2)}</td>
                                            <td className="py-6 text-center"><span className={`px-3 py-1 rounded-lg font-black font-mono text-xs ${p.stock < 10 ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>{p.stock}</span></td>
                                            <td className="py-6 text-center"><span className="px-3 py-1 rounded-lg font-black font-mono text-xs bg-orange-50 text-orange-600">{p.onHold || 0}</span></td>
                                            <td className="py-6 text-center"><span className="px-3 py-1 rounded-lg font-black font-mono text-xs bg-slate-100 text-slate-900">{p.stock + (p.onHold || 0)}</span></td>
                                            <td className="py-6 text-center">
                                                <button onClick={() => handleToggleProductStatus(p)} className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest transition-all ${p.isActive !== false ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-slate-200 text-slate-500 hover:bg-slate-300'}`}>
                                                    {p.isActive !== false ? 'Active' : 'Inactive'}
                                                </button>
                                            </td>
                                            <td className="py-6 text-right"><div className="flex justify-end gap-2"><button onClick={() => setEditingProduct(p)} className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button><button onClick={() => handleDeleteProduct(p.id)} className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button></div></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === 'orders' && (
                    <div className="p-10">
                        <div className="mb-10"><h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Order Fulfillment</h2><p className="text-slate-400 text-sm">Track shipments and update delivery status</p></div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 bg-slate-50 p-6 rounded-3xl border border-slate-100">
                            <input type="text" placeholder="Search by Order ID or Customer..." className="px-4 py-2 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium" value={filters.orders.search} onChange={e => setFilters({...filters, orders: {...filters.orders, search: e.target.value}})} />
                            <select className="px-4 py-2 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold uppercase tracking-wider" value={filters.orders.status} onChange={e => setFilters({...filters, orders: {...filters.orders, status: e.target.value}})}><option value="ALL">All Statuses</option><option value="ORDERED">Ordered</option><option value="DELIVERING">Delivering</option><option value="COMPLETED">Completed</option><option value="CANCELLED">Cancelled</option></select>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left min-w-[900px]">
                                <thead><tr className="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] border-b border-slate-100"><th className="pb-6">ID / Customer</th><th className="pb-6">Order Details</th><th className="pb-6 text-right">Total</th><th className="pb-6">Internal Note (Admin Only)</th><th className="pb-6 text-center">Status</th><th className="pb-6 text-right">Update</th></tr></thead>
                                <tbody className="text-sm">
                                    {filteredOrders.map(o => (
                                        <tr key={o.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors">
                                            <td className="py-6 cursor-pointer group" onClick={() => onNavigate(`order-detail-${o.id}`)}>
                                                <div className="font-mono text-[10px] text-slate-400 mb-1 group-hover:text-blue-600 transition-colors">{o.id}</div>
                                                <div className="font-black text-slate-900 group-hover:text-blue-600 transition-colors">{o.businessName}</div>
                                            </td>
                                            <td className="py-6"><div className="flex -space-x-2 mb-2">{o.items.slice(0,3).map((item, i) => (<img key={i} src={item.image} className="w-8 h-8 rounded-full border-2 border-white object-cover shadow-sm" alt="" title={item.name} />))}{o.items.length > 3 && <div className="w-8 h-8 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[10px] font-black text-slate-500">+{o.items.length - 3}</div>}</div><div className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">{new Date(o.date).toLocaleDateString()}</div></td>
                                            <td className="py-6 text-right font-mono font-black text-slate-900 text-lg">${o.total.toFixed(2)}</td>
                                            <td className="py-6 px-4"><textarea className="w-full p-2 bg-slate-50 border border-slate-100 rounded-lg text-xs font-medium focus:ring-2 focus:ring-blue-500/10 focus:bg-white transition-all outline-none resize-none" placeholder="Add private note..." defaultValue={o.adminNote || ''} onBlur={(e) => { if (e.target.value !== (o.adminNote || '')) handleUpdateOrder(o.id, o.status, e.target.value); }} /></td>
                                            <td className="py-6 text-center"><span className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-wider ${o.status === 'COMPLETED' ? 'bg-green-500 text-white shadow-lg shadow-green-100' : o.status === 'CANCELLED' ? 'bg-slate-200 text-slate-500' : 'bg-blue-600 text-white shadow-lg shadow-blue-100'}`}>{o.status}</span></td>
                                            <td className="py-6 text-right"><div className="flex items-center justify-end gap-2"><select value={o.status} disabled={savingStatus === o.id} onChange={(e) => handleUpdateOrder(o.id, e.target.value, o.adminNote || '')} className="bg-slate-100 border-none text-[10px] font-black uppercase tracking-tighter rounded-xl px-3 py-2 outline-none focus:ring-4 focus:ring-blue-500/10 cursor-pointer disabled:opacity-50"><option value="ORDERED">Ordered</option><option value="DELIVERING">Delivering</option><option value="COMPLETED">Completed</option><option value="CANCELLED">Cancelled</option></select></div></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === 'clients' && (
                    <div className="p-10">
                        <div className="mb-10"><h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Client Directory</h2><p className="text-slate-400 text-sm">Monitor user status and business accounts</p></div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 bg-slate-50 p-6 rounded-3xl border border-slate-100">
                            <input type="text" placeholder="Search by Name or Email..." className="px-4 py-2 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium" value={filters.clients.search} onChange={e => setFilters({...filters, clients: {...filters.clients, search: e.target.value}})} />
                            <select className="px-4 py-2 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold uppercase tracking-wider" value={filters.clients.status} onChange={e => setFilters({...filters, clients: {...filters.clients, status: e.target.value}})}><option value="ALL">All Account Statuses</option><option value="ACTIVE">Active Only</option><option value="DISABLED">Disabled Only</option></select>
                            <select className="px-4 py-2 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold uppercase tracking-wider" value={filters.clients.tag} onChange={e => setFilters({...filters, clients: {...filters.clients, tag: e.target.value}})}><option value="ALL">All Tags</option>{allTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}</select>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left min-w-[700px]">
                                <thead><tr className="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] border-b border-slate-100"><th className="pb-6">Business Partner</th><th className="pb-6">Tags</th><th className="pb-6">Account Status</th><th className="pb-6 text-right">Actions</th></tr></thead>
                                <tbody className="text-sm">
                                    {filteredClients.map(c => (
                                        <tr key={c.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors group cursor-pointer" onClick={() => setSelectedClient(c)}>
                                            <td className="py-6"><div className="flex items-center gap-4"><div className="w-10 h-10 bg-slate-900 text-white rounded-full flex items-center justify-center font-black text-xs uppercase">{(c.businessName || c.email || '??').substring(0,2)}</div><div><div className="font-black text-slate-900 group-hover:text-blue-600 transition-colors">{c.businessName || 'Unnamed Client'}</div><div className="text-xs text-slate-400 font-medium">{c.email}</div></div></div></td>
                                            <td className="py-6"><div className="flex flex-wrap gap-1">{c.tags && c.tags.length > 0 ? c.tags.map((t, i) => <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[8px] font-black uppercase rounded-md border border-blue-100">{t}</span>) : <span className="text-slate-300 italic text-[10px]">No tags</span>}</div></td>
                                            <td className="py-6"><span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${c.status === 'DISABLED' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>{c.status || 'ACTIVE'}</span></td>
                                            <td className="py-6 text-right" onClick={e => e.stopPropagation()}>
                                                <div className="flex justify-end gap-2">
                                                    <button onClick={() => handleToggleAdmin(c)} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${c.role === 'ADMIN' ? 'bg-purple-100 text-purple-600 hover:bg-purple-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>{c.role === 'ADMIN' ? 'Revoke Admin' : 'Make Admin'}</button>
                                                    <button onClick={() => handleToggleUser(c)} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${c.status === 'DISABLED' ? 'bg-green-600 text-white' : 'bg-red-50 text-red-600 hover:bg-red-600 hover:text-white'}`}>{c.status === 'DISABLED' ? 'Restore' : 'Suspend'}</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        )}

        {/* Client Detail Modal */}
        {selectedClient && (
            <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-xl z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-[3rem] shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-12 relative border border-white/20">
                    <button onClick={() => setSelectedClient(null)} className="absolute top-8 right-8 text-slate-300 hover:text-slate-900 transition-colors">
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    
                    <div className="flex items-center gap-6 mb-10">
                        <div className="w-20 h-20 bg-slate-900 text-white rounded-[2rem] flex items-center justify-center text-3xl font-black uppercase shadow-2xl shadow-slate-200">{(selectedClient.businessName || '??').substring(0,2)}</div>
                        <div>
                            <h2 className="text-4xl font-black text-slate-900 tracking-tight">{selectedClient.businessName}</h2>
                            <p className="text-slate-400 font-medium">{selectedClient.email} • ID: {selectedClient.id}</p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12">
                        <div className="space-y-8">
                            <section>
                                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">Contact & Shipping</h3>
                                <div className="bg-slate-50 p-6 rounded-3xl space-y-3">
                                    <p className="text-sm font-bold text-slate-700"><span className="text-slate-400 uppercase text-[9px] block mb-1">Contact Name</span>{selectedClient.contactName || 'N/A'}</p>
                                    <p className="text-sm font-bold text-slate-700"><span className="text-slate-400 uppercase text-[9px] block mb-1">Phone</span>{selectedClient.phone || 'N/A'}</p>
                                    <p className="text-sm font-bold text-slate-700"><span className="text-slate-400 uppercase text-[9px] block mb-1">Address</span>{selectedClient.address}<br/>{selectedClient.city}, {selectedClient.state} {selectedClient.zip}</p>
                                </div>
                            </section>

                            <section>
                                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">Client Tags</h3>
                                <div className="space-y-4">
                                    <div className="flex flex-wrap gap-2">
                                        {(selectedClient.tags || []).map((t, i) => (
                                            <span key={i} className="group flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-[10px] font-black uppercase rounded-xl shadow-lg shadow-blue-100">
                                                {t}
                                                <button onClick={() => setSelectedClient({...selectedClient, tags: (selectedClient.tags || []).filter(tag => tag !== t)})} className="hover:text-red-200 transition-colors">×</button>
                                            </span>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <input 
                                            type="text" 
                                            placeholder="Add new tag..." 
                                            className="flex-1 px-4 py-2 rounded-xl bg-slate-50 border-none ring-1 ring-slate-200 text-xs font-bold focus:ring-2 focus:ring-blue-500" 
                                            value={newTagInput}
                                            onChange={e => setNewTagInput(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddTag();
                                                }
                                            }}
                                        />
                                        <button 
                                            onClick={handleAddTag}
                                            type="button"
                                            className="px-4 py-2 bg-slate-900 text-white rounded-xl font-black text-xs uppercase hover:bg-slate-700 transition-colors"
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                            </section>
                        </div>

                        <div className="space-y-8">
                            <section>
                                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">Internal Notes (Admin Only)</h3>
                                <textarea 
                                    className="w-full h-64 p-6 bg-slate-50 border-none rounded-[2rem] text-sm font-medium focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all outline-none resize-none"
                                    placeholder="Start typing client relationship notes here..."
                                    value={selectedClient.adminNotes || ''}
                                    onChange={e => setSelectedClient({...selectedClient, adminNotes: e.target.value})}
                                />
                            </section>
                        </div>
                    </div>

                    <div className="mt-12 flex justify-end gap-4">
                        <button 
                            onClick={() => handleToggleAdmin(selectedClient)}
                            className={`px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${selectedClient.role === 'ADMIN' ? 'bg-purple-100 text-purple-600 hover:bg-purple-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                        >
                            {selectedClient.role === 'ADMIN' ? 'Revoke Admin' : 'Grant Admin Access'}
                        </button>
                        <button 
                            onClick={handleSaveClientDetail}
                            disabled={savingClient}
                            className="bg-blue-600 text-white px-12 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-200 hover:bg-blue-700 hover:-translate-y-0.5 transition-all disabled:opacity-50"
                        >
                            {savingClient ? 'Saving...' : 'Save & Sync Client'}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* Product Editor Modal */}
        {editingProduct && (
            <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-xl z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-[3rem] shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto p-12 relative border border-white/20">
                    <button onClick={() => setEditingProduct(null)} className="absolute top-8 right-8 text-slate-300 hover:text-slate-900 transition-colors">
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    <h2 className="text-3xl font-black mb-2 uppercase tracking-tight text-slate-900">{editingProduct.id ? 'Refine Product' : 'Onboard New Product'}</h2>
                    <p className="text-slate-400 mb-10 text-sm font-medium">Configure market pricing and inventory specifications.</p>

                    <form onSubmit={handleSaveProduct} className="space-y-8">
                        <div className="grid lg:grid-cols-2 gap-12">
                            <div className="space-y-8">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] mb-4">Product Video (Default Preview)</label>
                                    {editingProduct.video ? (
                                        <div className="relative aspect-video rounded-3xl overflow-hidden border-2 border-blue-500 shadow-xl bg-black">
                                            <video src={editingProduct.video} className="w-full h-full object-contain" controls autoPlay />
                                            <button type="button" onClick={() => setEditingProduct({...editingProduct, video: undefined})} className="absolute top-4 right-4 bg-red-500 text-white rounded-full p-2 shadow-lg hover:bg-red-600 transition-colors">
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            </button>
                                        </div>
                                    ) : (
                                        <button type="button" disabled={uploading} onClick={() => videoInputRef.current?.click()} className="w-full aspect-video rounded-3xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition-all gap-3 bg-slate-50/50">
                                            {uploading ? <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div> : <><span className="text-4xl">🎥</span><span className="text-xs font-black uppercase tracking-widest">Upload Promotional Video</span></>}
                                        </button>
                                    )}
                                    <input type="file" ref={videoInputRef} className="hidden" accept="video/*" onChange={(e) => handleFileUpload(e, 'video')} />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] mb-4">Image Gallery & Previews</label>
                                    <div className="grid grid-cols-3 gap-4">
                                        {/* Show primary image if it exists and isn't already in gallery */}
                                        {editingProduct.image && (
                                            <div className="relative aspect-square">
                                                <img src={editingProduct.image} className="w-full h-full object-cover rounded-2xl border-4 border-blue-500 shadow-lg" alt="Primary" />
                                                <div className="absolute top-2 left-2 bg-blue-500 text-white text-[8px] font-black uppercase px-2 py-1 rounded-md">Primary</div>
                                            </div>
                                        )}
                                        
                                        {/* Show other images */}
                                        {editingProduct.images?.filter(url => url && url !== editingProduct.image).map((url, idx) => (
                                            <div key={idx} className="relative aspect-square group">
                                                <img src={url} className="w-full h-full object-cover rounded-2xl border border-slate-100 shadow-sm" alt="" />
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl flex items-center justify-center gap-2">
                                                    <button type="button" onClick={() => setEditingProduct({...editingProduct, image: url})} className="bg-blue-500 text-white text-[9px] font-black uppercase px-3 py-2 rounded-xl shadow-lg hover:bg-blue-600 transition-colors transform hover:scale-105">
                                                        Make Primary
                                                    </button>
                                                    <button type="button" onClick={() => removeImage(url)} className="bg-red-500 text-white p-2 rounded-xl shadow-lg hover:bg-red-600 transition-colors transform hover:scale-105">
                                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                        
                                        <button type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()} className="aspect-square rounded-2xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition-all gap-2 bg-slate-50/50">
                                            {uploading ? <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full"></div> : <><svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg><span className="text-[10px] font-black uppercase">Add Photo</span></>}
                                        </button>
                                    </div>
                                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'image')} />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] mb-4">Technical Documents (PDF/Manuals)</label>
                                    <div className="space-y-3">
                                        {editingProduct.documents?.map((doc, idx) => (
                                            <div key={idx} className="flex items-center justify-between bg-slate-50 p-4 rounded-2xl border border-slate-100 group">
                                                <div className="flex items-center gap-3">
                                                    <span className="text-2xl">📄</span>
                                                    <div>
                                                        <div className="text-xs font-bold text-slate-900 truncate max-w-[200px]">{doc.name}</div>
                                                        <div className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">PDF Document</div>
                                                    </div>
                                                </div>
                                                <button type="button" onClick={() => {
                                                    const updatedDocs = (editingProduct.documents || []).filter((_, i) => i !== idx);
                                                    setEditingProduct({ ...editingProduct, documents: updatedDocs });
                                                }} className="p-2 text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                </button>
                                            </div>
                                        ))}
                                        
                                        <button type="button" disabled={uploading} onClick={() => docInputRef.current?.click()} className="w-full py-4 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition-all gap-2 bg-slate-50/50">
                                            {uploading ? <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full"></div> : <><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg><span className="text-[10px] font-black uppercase tracking-widest">Attach Document</span></>}
                                        </button>
                                    </div>
                                    <input type="file" ref={docInputRef} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => handleFileUpload(e, 'document')} />
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-400 mb-2 tracking-widest">Product Name</label>
                                    <input className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-bold text-slate-900" value={editingProduct.name || ''} onChange={e => setEditingProduct({...editingProduct, name: e.target.value})} required />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-400 mb-2 tracking-widest">Full Description</label>
                                    <textarea className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-medium text-slate-600 h-24" value={editingProduct.description || ''} onChange={e => setEditingProduct({...editingProduct, description: e.target.value})} required />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-black uppercase text-slate-400 mb-2 tracking-widest">Price ($)</label>
                                        <input type="number" step="0.01" className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-black text-blue-600 text-lg" value={editingProduct.price || 0} onChange={e => setEditingProduct({...editingProduct, price: parseFloat(e.target.value)})} required />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black uppercase text-slate-400 mb-2 tracking-widest">Cost Basis ($)</label>
                                        <input type="number" step="0.01" className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-black text-slate-400 text-lg" value={editingProduct.costPrice || 0} onChange={e => setEditingProduct({...editingProduct, costPrice: parseFloat(e.target.value)})} />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-black uppercase text-slate-400 mb-2 tracking-widest">Inventory Level</label>
                                        <input type="number" className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-black text-slate-900 text-lg" value={editingProduct.stock || 0} onChange={e => setEditingProduct({...editingProduct, stock: parseInt(e.target.value)})} required />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black uppercase text-slate-400 mb-2 tracking-widest">Target Market</label>
                                        <select className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-bold text-slate-900" value={editingProduct.category || 'CLINIC'} onChange={e => setEditingProduct({...editingProduct, category: e.target.value as any})}>
                                            <option value="CLINIC">Dental Clinic</option><option value="LAB">Dental Laboratory</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="p-8 bg-slate-50 rounded-[2rem] space-y-6">
                                    <label className="flex items-center space-x-4 cursor-pointer group">
                                        <div className={`w-12 h-6 rounded-full transition-all relative ${editingProduct.onPromotion ? 'bg-red-500' : 'bg-slate-300'}`}>
                                            <input type="checkbox" className="hidden" checked={editingProduct.onPromotion || false} onChange={e => setEditingProduct({...editingProduct, onPromotion: e.target.checked})} />
                                            <div className={`absolute top-1 bg-white w-4 h-4 rounded-full transition-all ${editingProduct.onPromotion ? 'left-7' : 'left-1'}`} />
                                        </div>
                                        <span className="font-black text-xs uppercase tracking-[0.2em] text-slate-700">Active Promotion</span>
                                    </label>
                                    { editingProduct.onPromotion && (
                                        <div className="flex items-center gap-6 animate-fade-in">
                                            <div className="flex-1">
                                                <label className="block text-[10px] font-black uppercase text-slate-400 mb-2">Discount Percentage (%)</label>
                                                <input type="number" className="w-full px-6 py-4 bg-white border-none rounded-2xl font-black text-red-600 text-xl shadow-inner" value={editingProduct.discount || 0} onChange={e => setEditingProduct({...editingProduct, discount: parseFloat(e.target.value)})} />
                                            </div>
                                            <div className="text-3xl">🔥</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button type="submit" className="w-full bg-slate-900 text-white py-6 rounded-[2rem] font-black text-sm uppercase tracking-[0.4em] shadow-2xl hover:bg-black hover:-translate-y-1 transition-all active:scale-95">Commit to Live Database</button>
                    </form>
                </div>
            </div>
        )}
        {/* Import Preview Page (Full Screen) */}
        {showImportModal && (
            <div className="fixed inset-0 z-50 bg-slate-50 flex flex-col overflow-hidden animate-fade-in">
                {/* Header */}
                <div className="bg-white border-b border-slate-200 px-8 py-6 flex justify-between items-center shadow-sm z-10">
                    <div>
                        <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-4">
                            <span className="bg-slate-900 text-white w-10 h-10 rounded-xl flex items-center justify-center text-lg">⬆</span>
                            Bulk Import Review
                        </h2>
                        <p className="text-slate-500 font-medium mt-1 ml-14">Verify and edit your data before committing to the live database.</p>
                    </div>
                    <div className="flex gap-4">
                        <div className="px-6 py-2 bg-green-50 rounded-xl border border-green-100 flex flex-col items-center">
                            <span className="text-xl font-black text-green-600">{importPreview.filter(i => i.status === 'NEW').length}</span>
                            <span className="text-[9px] uppercase font-black text-green-800 tracking-wider">New</span>
                        </div>
                        <div className="px-6 py-2 bg-blue-50 rounded-xl border border-blue-100 flex flex-col items-center">
                            <span className="text-xl font-black text-blue-600">{importPreview.filter(i => i.status === 'UPDATE').length}</span>
                            <span className="text-[9px] uppercase font-black text-blue-800 tracking-wider">Updates</span>
                        </div>
                        <div className="px-6 py-2 bg-slate-100 rounded-xl border border-slate-200 flex flex-col items-center opacity-75">
                            <span className="text-xl font-black text-slate-600">{importPreview.filter(i => i.status === 'NO_CHANGE').length}</span>
                            <span className="text-[9px] uppercase font-black text-slate-500 tracking-wider">No Change</span>
                        </div>
                        <div className="px-6 py-2 bg-red-50 rounded-xl border border-red-100 flex flex-col items-center">
                            <span className="text-xl font-black text-red-600">{importPreview.filter(i => i.status === 'DEACTIVATE').length}</span>
                            <span className="text-[9px] uppercase font-black text-red-800 tracking-wider">Deactivate</span>
                        </div>
                    </div>
                </div>

                {/* Editable Table */}
                <div className="flex-1 overflow-auto p-8 pb-40">
                    <div className="bg-white rounded-[2rem] shadow-xl border border-slate-200 overflow-hidden min-w-max">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-slate-50 sticky top-0 z-10 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                <tr>
                                    <th className="p-4 border-b border-r border-slate-100 bg-slate-50 sticky left-0 z-20 w-32">Status</th>
                                    <th className="p-4 border-b border-r border-slate-100 text-center w-20">Active</th>
                                    <th className="p-4 border-b border-r border-slate-100 w-64">Product Name</th>
                                    <th className="p-4 border-b border-r border-slate-100 w-40">Category</th>
                                    <th className="p-4 border-b border-r border-slate-100 w-32 text-right">Price</th>
                                    <th className="p-4 border-b border-r border-slate-100 w-32 text-right">Cost</th>
                                    <th className="p-4 border-b border-r border-slate-100 w-24 text-center">Available</th>
                                    <th className="p-4 border-b border-r border-slate-100 w-24 text-center">Reserved</th>
                                    <th className="p-4 border-b border-r border-slate-100 w-20 text-center">Promo</th>
                                    <th className="p-4 border-b border-slate-100 w-24 text-right">Discount %</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm font-medium">
                                {importPreview.map((item, idx) => {
                                    const isNoChange = item.status === 'NO_CHANGE';
                                    const getCellClass = (field: keyof Product) => {
                                        const changed = item.originalData && String(item.originalData[field] ?? '') !== String(item.data[field] ?? '');
                                        return changed ? 'bg-yellow-50 ring-2 ring-yellow-400/50 rounded-lg' : '';
                                    };
                                    
                                    return (
                                    <tr key={idx} className={`hover:bg-slate-50 transition-colors group ${
                                        item.status === 'DEACTIVATE' ? 'bg-red-50/30' : 
                                        isNoChange ? 'opacity-50 grayscale hover:grayscale-0 hover:opacity-100' : ''
                                    }`}>
                                        <td className="p-3 border-b border-r border-slate-100 sticky left-0 bg-white group-hover:bg-slate-50 z-10">
                                            <span className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider block text-center w-full ${
                                                item.status === 'NEW' ? 'bg-green-100 text-green-700' :
                                                item.status === 'UPDATE' ? 'bg-blue-100 text-blue-700' :
                                                item.status === 'DEACTIVATE' ? 'bg-red-100 text-red-700' :
                                                'bg-slate-100 text-slate-500'
                                            }`}>{item.status.replace('_', ' ')}</span>
                                        </td>
                                        <td className="p-3 border-b border-r border-slate-100 text-center">
                                            <input 
                                                type="checkbox" 
                                                checked={item.data.isActive !== false} 
                                                onChange={e => updatePreviewItem(idx, 'isActive', e.target.checked)}
                                                className={`w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer ${getCellClass('isActive')}`}
                                            />
                                        </td>
                                        <td className="p-3 border-b border-r border-slate-100">
                                            <input 
                                                type="text" 
                                                value={item.data.name || ''} 
                                                onChange={e => updatePreviewItem(idx, 'name', e.target.value)}
                                                className={`w-full bg-transparent border-none focus:ring-2 focus:ring-blue-500/20 rounded-lg px-2 py-1 font-bold text-slate-700 ${getCellClass('name')}`}
                                            />
                                        </td>
                                        <td className="p-3 border-b border-r border-slate-100">
                                            <select 
                                                value={item.data.category} 
                                                onChange={e => updatePreviewItem(idx, 'category', e.target.value)}
                                                className={`w-full bg-transparent border-none focus:ring-2 focus:ring-blue-500/20 rounded-lg px-2 py-1 text-xs font-black uppercase tracking-wider text-slate-500 ${getCellClass('category')}`}
                                            >
                                                <option value="CLINIC">Clinic</option>
                                                <option value="LAB">Lab</option>
                                            </select>
                                        </td>
                                        <td className="p-3 border-b border-r border-slate-100 text-right">
                                            <input 
                                                type="number" 
                                                step="0.01"
                                                value={item.data.price || 0} 
                                                onChange={e => updatePreviewItem(idx, 'price', parseFloat(e.target.value))}
                                                className={`w-full bg-transparent border-none focus:ring-2 focus:ring-blue-500/20 rounded-lg px-2 py-1 text-right font-mono font-bold text-slate-700 ${getCellClass('price')}`}
                                            />
                                        </td>
                                        <td className="p-3 border-b border-r border-slate-100 text-right">
                                            <input 
                                                type="number" 
                                                step="0.01"
                                                value={item.data.costPrice || 0} 
                                                onChange={e => updatePreviewItem(idx, 'costPrice', parseFloat(e.target.value))}
                                                className={`w-full bg-transparent border-none focus:ring-2 focus:ring-blue-500/20 rounded-lg px-2 py-1 text-right font-mono text-slate-500 ${getCellClass('costPrice')}`}
                                            />
                                        </td>
                                        <td className="p-3 border-b border-r border-slate-100 text-center">
                                            <input 
                                                type="number" 
                                                value={item.data.stock || 0} 
                                                onChange={e => updatePreviewItem(idx, 'stock', parseInt(e.target.value))}
                                                className={`w-full bg-transparent border-none focus:ring-2 focus:ring-blue-500/20 rounded-lg px-2 py-1 text-center font-bold ${item.data.stock < 10 ? 'text-red-600' : 'text-slate-700'} ${getCellClass('stock')}`}
                                            />
                                        </td>
                                        <td className="p-3 border-b border-r border-slate-100 text-center">
                                            <input 
                                                type="number" 
                                                value={item.data.onHold || 0} 
                                                onChange={e => updatePreviewItem(idx, 'onHold', parseInt(e.target.value))}
                                                className={`w-full bg-transparent border-none focus:ring-2 focus:ring-blue-500/20 rounded-lg px-2 py-1 text-center font-bold text-orange-600 ${getCellClass('onHold')}`}
                                            />
                                        </td>
                                        <td className="p-3 border-b border-r border-slate-100 text-center">
                                            <input 
                                                type="checkbox" 
                                                checked={item.data.onPromotion || false} 
                                                onChange={e => updatePreviewItem(idx, 'onPromotion', e.target.checked)}
                                                className={`w-5 h-5 rounded border-slate-300 text-red-500 focus:ring-red-500 cursor-pointer ${getCellClass('onPromotion')}`}
                                            />
                                        </td>
                                        <td className="p-3 border-b border-slate-100 text-right">
                                            {item.data.onPromotion ? (
                                                <input 
                                                    type="number" 
                                                    value={item.data.discount || 0} 
                                                    onChange={e => updatePreviewItem(idx, 'discount', parseFloat(e.target.value))}
                                                    className={`w-full bg-transparent border-none focus:ring-2 focus:ring-red-500/20 rounded-lg px-2 py-1 text-right font-black text-red-600 ${getCellClass('discount')}`}
                                                />
                                            ) : <span className="text-slate-200 text-center block">-</span>}
                                        </td>
                                    </tr>
                                )})}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="bg-white/90 backdrop-blur-xl border-t border-slate-200 p-8 flex justify-end gap-6 shadow-[0_-20px_60px_rgba(0,0,0,0.1)] z-30 sticky bottom-0">
                    <button 
                        onClick={() => setShowImportModal(false)} 
                        className="px-10 py-5 rounded-2xl font-bold text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors text-xs uppercase tracking-widest border border-slate-100 hover:border-slate-200"
                    >
                        Cancel Import
                    </button>
                    <button 
                        onClick={handleConfirmImport} 
                        disabled={processingImport}
                        className="bg-slate-900 text-white px-12 py-5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl hover:bg-black hover:-translate-y-1 transition-all disabled:opacity-50 flex items-center gap-3"
                    >
                        {processingImport ? (
                            <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"/> Processing...</>
                        ) : (
                            <>Save & Sync Database <span className="opacity-50">→</span></>
                        )}
                    </button>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
